'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '../PageLayout';
import { COMPANY_DATA } from '../../config/companyData';

const initialForm = {
  name: '',
  email: '',
  company: '',
  topic: 'general',
  message: '',
};

function MailIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>;
}

function ClockIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3.4 2" /></svg>;
}

export default function Contact() {
  const { t } = useTranslation('contact');
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setSubmitted(false);
  };

  const submit = (event) => {
    event.preventDefault();
    const subject = `${t('contact_form.subject_prefix')} ${form.topic}: ${form.name}`;
    const body = [
      `${t('contact_form.name')}: ${form.name}`,
      `${t('contact_form.email')}: ${form.email}`,
      `${t('contact_form.company')}: ${form.company || '—'}`,
      '',
      form.message,
    ].join('\n');

    setSubmitted(true);
    // URLSearchParams encodes spaces as '+' (form-encoding), which mail
    // clients show literally instead of decoding — encodeURIComponent uses
    // %20 for spaces, which mailto links (RFC 6068) actually expect.
    const query = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = `mailto:${COMPANY_DATA.email}?${query}`;
  };

  return (
    <PageLayout presentLikeHome>
      <link rel="stylesheet" href="/assets/css/contact-layout-2.css" />
      <main className="contact-v2">
        <section className="contact-v2__hero">
          <div className="contact-v2__container contact-v2__grid">
            <div className="contact-v2__copy">
              <p className="contact-v2__eyebrow">{t('contact_form.eyebrow')}</p>
              <h1>{t('contact_form.title')}</h1>
              <p className="contact-v2__intro">{t('contact_form.description')}</p>

              <div className="contact-v2__details">
                <a className="contact-v2__detail" href={`mailto:${COMPANY_DATA.email}`}>
                  <span className="contact-v2__detail-icon"><MailIcon /></span>
                  <span><small>{t('contact_form.email_label')}</small>{COMPANY_DATA.email}</span>
                </a>
                <div className="contact-v2__detail">
                  <span className="contact-v2__detail-icon"><ClockIcon /></span>
                  <span><small>{t('contact_form.response_label')}</small>{t('contact_form.response_time')}</span>
                </div>
              </div>

              <div className="contact-v2__note">
                <span aria-hidden="true">✦</span>
                <p>{t('contact_form.note')}</p>
              </div>
            </div>

            <section className="contact-v2__form-card" aria-labelledby="contact-form-heading">
              <div className="contact-v2__form-heading">
                <span className="contact-v2__form-mark" aria-hidden="true"><MailIcon /></span>
                <div>
                  <h2 id="contact-form-heading">{t('contact_form.form_title')}</h2>
                  <p>{t('contact_form.form_description')}</p>
                </div>
              </div>

              <form onSubmit={submit}>
                <div className="contact-v2__two-fields">
                  <label>{t('contact_form.name')}<input name="name" value={form.name} onChange={updateField} autoComplete="name" required /></label>
                  <label>{t('contact_form.email')}<input name="email" type="email" value={form.email} onChange={updateField} autoComplete="email" required /></label>
                </div>
                <label>{t('contact_form.company')}<input name="company" value={form.company} onChange={updateField} autoComplete="organization" /></label>
                <label>{t('contact_form.topic')}<select name="topic" value={form.topic} onChange={updateField}><option value="general">{t('contact_form.topic_general')}</option><option value="sales">{t('contact_form.topic_sales')}</option><option value="support">{t('contact_form.topic_support')}</option><option value="partnership">{t('contact_form.topic_partnership')}</option></select></label>
                <label>{t('contact_form.message')}<textarea name="message" value={form.message} onChange={updateField} required rows="5" /></label>
                <button type="submit">{t('contact_form.submit')} <span aria-hidden="true">→</span></button>
                <p className="contact-v2__form-note">{submitted ? t('contact_form.opened_email') : t('contact_form.submit_note')}</p>
              </form>
            </section>
          </div>
        </section>
      </main>
    </PageLayout>
  );
}
